import { useCallback } from 'react';
import {
  Grid,
  ScrollablePanelContent,
  ToggledSetting,
  ToggledSettings,
  usePubSub,
  useSnackbar,
  useTranslations,
} from '@uniqkey-frontend/shared-app';
import {
  useGetOrganizationSecuritySettings,
  useUpdateOrganizationSecuritySettings,
} from '../../../../hooks/reactQuery';
import PubSubEventEnum from '../../../../enums/PubSubEventEnum';

const SecurityTab = () => {
  const { t } = useTranslations();

  const { showError, showSuccess } = useSnackbar();
  const {
    data: settings, isLoading, isError, refetch,
  } = useGetOrganizationSecuritySettings({
    onError: () => {
      showError({ text: t('common.somethingWentWrong') });
    },
  });
  const {
    mutate,
    isLoading: isUpdating,
  } = useUpdateOrganizationSecuritySettings({ useOptimisticUpdates: true });

  const handleSettingChange = useCallback((name: string, checked: boolean) => {
    if (isUpdating) {
      return;
    }
    mutate(
      { ...settings!, [name]: checked },
      {
        onError: () => showError({ text: t('common.somethingWentWrong') }),
        onSuccess: () => {
          showSuccess({ text: t('common.settingsUpdatedToast') });
        },
      },
    );
  }, [isUpdating, mutate, settings, showError, showSuccess, t]);

  const handleRefetch = useCallback(() => refetch(), [refetch]);
  usePubSub(PubSubEventEnum.DATA_SYNCHRONIZATION_SECURITY_SETTINGS, handleRefetch);

  if (isError) {
    return null;
  }

  const {
    trustedBrowserAllowed,
    exportAllowed,
    autoSaveAllowed,
    secondaryDeviceAllowed,
    trustedPortalAllowed,
  } = settings ?? {};

  return (
    <ScrollablePanelContent p={3}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid container item xs={6} rowGap={3}>
          <ToggledSettings isLoading={isLoading}>
            <ToggledSetting
              name="trustedBrowserAllowed"
              title={t('settingsPage.securityTab.settings.trustedBrowserAllowed.title')}
              description={t('settingsPage.securityTab.settings.trustedBrowserAllowed.description')}
              checked={trustedBrowserAllowed}
              onChange={handleSettingChange}
            />
          </ToggledSettings>
          <ToggledSettings isLoading={isLoading}>
            <ToggledSetting
              name="secondaryDeviceAllowed"
              title={t('settingsPage.securityTab.settings.secondaryDeviceAllowed.title')}
              description={
              t('settingsPage.securityTab.settings.secondaryDeviceAllowed.description')
            }
              checked={secondaryDeviceAllowed}
              onChange={handleSettingChange}
            />
          </ToggledSettings>
          <ToggledSettings isLoading={isLoading}>
            <ToggledSetting
              name="trustedPortalAllowed"
              title={t('settingsPage.securityTab.settings.trustedPortalAllowed.title')}
              description={
                t('settingsPage.securityTab.settings.trustedPortalAllowed.description')
              }
              checked={trustedPortalAllowed}
              onChange={handleSettingChange}
            />
          </ToggledSettings>
        </Grid>
        <Grid container item xs={6} rowGap={3}>
          <ToggledSettings isLoading={isLoading}>
            <ToggledSetting
              name="exportAllowed"
              title={t('settingsPage.securityTab.settings.exportAllowed.title')}
              description={t('settingsPage.securityTab.settings.exportAllowed.description')}
              checked={exportAllowed}
              onChange={handleSettingChange}
            />
          </ToggledSettings>
          <ToggledSettings isLoading={isLoading}>
            <ToggledSetting
              name="autoSaveAllowed"
              title={t('settingsPage.securityTab.settings.autoSaveAllowed.title')}
              description={t('settingsPage.securityTab.settings.autoSaveAllowed.description')}
              checked={autoSaveAllowed}
              onChange={handleSettingChange}
            />
          </ToggledSettings>
        </Grid>
      </Grid>
    </ScrollablePanelContent>
  );
};

export default SecurityTab;
